import React, { useState, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import YouTube from '@tiptap/extension-youtube';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  List,
  ListOrdered,
  Quote,
  Link as LinkIcon,
  Image as ImageIcon,
  Youtube,
  Undo,
  Redo,
  Code,
  Table as TableIcon,
  Superscript as SuperscriptIcon,
  Subscript as SubscriptIcon,
  X,
  Plus,
  Trash,
  Info,
  AlertTriangle,
  CheckCircle,
  Minus,
  Maximize,
  PaintBucket,
  Type,
  ChevronDown,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useToast } from "@/hooks/use-toast";

interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
}

export const RichTextEditor = ({ content, onChange }: RichTextEditorProps) => {
  const { toast } = useToast();
  const [linkUrl, setLinkUrl] = useState('');
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [imageAlt, setImageAlt] = useState('');
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [youtubeDialogOpen, setYoutubeDialogOpen] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);
  
  const editor = useEditor({
    extensions: [
      StarterKit,
      Image.configure({
        HTMLAttributes: {
          class: 'rounded-md max-w-full',
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary underline',
        },
      }),
      YouTube.configure({
        width: 640,
        height: 360,
        HTMLAttributes: {
          class: 'w-full aspect-video my-4',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Underline,
      TextStyle,
      Color,
      Highlight.configure({
        multicolor: true,
      }),
      Subscript,
      Superscript,
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  if (!editor) {
    return <div>Loading editor...</div>;
  }

  const handleImageUpload = async () => {
    if (!imageFile) {
      if (!imageUrl) {
        toast({
          title: "No image selected",
          description: "Please provide an image URL or upload a file",
          variant: "destructive",
        });
        return;
      }
      
      editor.chain().focus().setImage({ src: imageUrl, alt: imageAlt }).run();
      setImageDialogOpen(false);
      setImageUrl('');
      setImageAlt('');
      setImageFile(null);
      return;
    }
    
    setIsUploading(true);
    try {
      const fileExt = imageFile.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2, 15)}_${Date.now().toString(36)}.${fileExt}`;
      
      console.log('Uploading image to cms-images bucket...');
      const { data, error } = await supabase.storage
        .from('cms-images')
        .upload(`content/${fileName}`, imageFile, { upsert: false });
        
      if (error) {
        console.error('Error uploading image:', error);
        throw error;
      }
      
      console.log('Upload successful, getting public URL...');
      const { data: publicUrlData } = supabase.storage
        .from('cms-images')
        .getPublicUrl(`content/${fileName}`);
      
      if (publicUrlData && publicUrlData.publicUrl) {
        const src = publicUrlData.publicUrl;
        console.log('Image URL:', src);
        editor.chain().focus().setImage({ src, alt: imageAlt }).run();
      }
      
      setImageDialogOpen(false);
      setImageUrl('');
      setImageAlt('');
      setImageFile(null);
      
      toast({
        title: "Image uploaded",
        description: "The image was successfully uploaded and inserted",
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: "Upload failed",
        description: error instanceof Error ? error.message : "Failed to upload image. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const insertTable = () => {
    editor.chain().focus().insertContent('<table><tbody><tr><td></td><td></td><td></td></tr><tr><td></td><td></td><td></td></tr><tr><td></td><td></td><td></td></tr></tbody></table>').run();
  };

  const insertLink = () => {
    if (!linkUrl) return;
    
    let href = linkUrl;
    if (!/^https?:\/\//i.test(href)) {
      href = 'http://' + href;
    }
    
    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href })
      .run();
      
    setLinkDialogOpen(false);
    setLinkUrl('');
  };
  
  const insertYoutubeVideo = () => {
    if (!youtubeUrl) return;
    
    editor.chain().focus().setYoutubeVideo({
      src: youtubeUrl,
      width: 640,
      height: 360,
    }).run();
    
    setYoutubeDialogOpen(false);
    setYoutubeUrl('');
  };

  const addInfoCallout = () => {
    editor.chain().focus().insertContent(`
      <div class="flex p-4 mb-4 border-l-4 border-blue-500 bg-blue-50 rounded">
        <div class="text-blue-500 mr-3"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-info"><circle cx="12" cy="12" r="10"></circle><path d="M12 16v-4"></path><path d="M12 8h.01"></path></svg></div>
        <div>
          <p>Information callout</p>
        </div>
      </div>
    `).run();
  };

  const addWarningCallout = () => {
    editor.chain().focus().insertContent(`
      <div class="flex p-4 mb-4 border-l-4 border-yellow-500 bg-yellow-50 rounded">
        <div class="text-yellow-500 mr-3"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-alert-triangle"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path><path d="M12 9v4"></path><path d="M12 17h.01"></path></svg></div>
        <div>
          <p>Warning callout</p>
        </div>
      </div>
    `).run();
  };

  const addSuccessCallout = () => {
    editor.chain().focus().insertContent(`
      <div class="flex p-4 mb-4 border-l-4 border-green-500 bg-green-50 rounded">
        <div class="text-green-500 mr-3"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-check-circle"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><path d="m9 11 3 3L22 4"></path></svg></div>
        <div>
          <p>Success callout</p>
        </div>
      </div>
    `).run();
  };
  
  const addAccordion = () => {
    editor.chain().focus().insertContent(`
      <details class="border rounded mb-4">
        <summary class="p-4 cursor-pointer font-medium bg-muted/50 hover:bg-muted/70">Toggle content</summary>
        <div class="p-4">
          <p>This content can be expanded or collapsed.</p>
        </div>
      </details>
    `).run();
  };
  
  const addButton = () => {
    editor.chain().focus().insertContent(`
      <div class="my-4">
        <a href="http://example.com" class="inline-flex items-center justify-center rounded-md bg-primary px-6 py-3 text-white hover:bg-primary/90 font-medium text-sm">Button text</a>
      </div>
    `).run();
  };
  
  const addHorizontalRule = () => {
    editor.chain().focus().setHorizontalRule().run();
  };

  const headingLevels = [1, 2, 3, 4];
  
  return (
    <div className={`border rounded-md ${fullScreen ? 'fixed inset-0 z-50 bg-background p-4 overflow-y-auto' : ''}`}>
      <div className="bg-muted/20 border-b p-1 sticky top-0 z-10 flex flex-wrap gap-1 items-center">
        <TooltipProvider delayDuration={300}>
          <div className="flex flex-wrap gap-1 items-center">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => editor.chain().focus().toggleBold().run()}
                  className={editor.isActive('bold') ? 'bg-muted' : ''}
                >
                  <Bold size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Bold</TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => editor.chain().focus().toggleItalic().run()}
                  className={editor.isActive('italic') ? 'bg-muted' : ''}
                >
                  <Italic size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Italic</TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => editor.chain().focus().toggleUnderline().run()}
                  className={editor.isActive('underline') ? 'bg-muted' : ''}
                >
                  <UnderlineIcon size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Underline</TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => editor.chain().focus().toggleStrike().run()}
                  className={editor.isActive('strike') ? 'bg-muted' : ''}
                >
                  <Strikethrough size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Strikethrough</TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => editor.chain().focus().toggleSuperscript().run()}
                  className={editor.isActive('superscript') ? 'bg-muted' : ''}
                >
                  <SuperscriptIcon size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Superscript</TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => editor.chain().focus().toggleSubscript().run()}
                  className={editor.isActive('subscript') ? 'bg-muted' : ''}
                >
                  <SubscriptIcon size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Subscript</TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => editor.chain().focus().unsetAllMarks().run()}
                >
                  <X size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Clear formatting</TooltipContent>
            </Tooltip>
            
            <Separator orientation="vertical" className="h-8" />
            
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="sm">
                  <Type size={16} className="mr-1" /> Heading
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <div className="flex flex-col">
                  {headingLevels.map((level) => (
                    <Button
                      key={level}
                      variant="ghost"
                      onClick={() => editor.chain().focus().toggleHeading({ level: level as 1 | 2 | 3 | 4 }).run()}
                      className={`justify-start ${editor.isActive('heading', { level }) ? 'bg-muted' : ''}`}
                    >
                      {level === 1 && <Heading1 size={16} className="mr-2" />}
                      {level === 2 && <Heading2 size={16} className="mr-2" />}
                      {level === 3 && <Heading3 size={16} className="mr-2" />}
                      {level === 4 && <Heading4 size={16} className="mr-2" />}
                      Heading {level}
                    </Button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
            
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="sm">
                  <PaintBucket size={16} className="mr-1" /> Colors
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64">
                <Tabs defaultValue="text">
                  <TabsList className="w-full">
                    <TabsTrigger value="text" className="flex-1">Text</TabsTrigger>
                    <TabsTrigger value="highlight" className="flex-1">Highlight</TabsTrigger>
                  </TabsList>
                  <TabsContent value="text" className="p-0 mt-2">
                    <div className="grid grid-cols-5 gap-2">
                      {['#000000', '#ff0000', '#0000ff', '#008000', '#ffa500', '#800080', '#a52a2a', '#808080', '#ffffff'].map((color) => (
                        <Button
                          key={color}
                          variant="outline"
                          className="w-8 h-8 p-0"
                          style={{ backgroundColor: color }}
                          onClick={() => editor.commands.setColor(color)}
                        />
                      ))}
                    </div>
                  </TabsContent>
                  <TabsContent value="highlight" className="p-0 mt-2">
                    <div className="grid grid-cols-5 gap-2">
                      {['#ffff00', '#ffd700', '#ffb6c1', '#add8e6', '#90ee90', '#d3d3d3', '#f0f8ff'].map((color) => (
                        <Button
                          key={color}
                          variant="outline"
                          className="w-8 h-8 p-0"
                          style={{ backgroundColor: color }}
                          onClick={() => editor.commands.setHighlight({ color })}
                        />
                      ))}
                    </div>
                  </TabsContent>
                </Tabs>
              </PopoverContent>
            </Popover>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => editor.chain().focus().setTextAlign('left').run()}
                  className={editor.isActive({ textAlign: 'left' }) ? 'bg-muted' : ''}
                >
                  <AlignLeft size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Align left</TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => editor.chain().focus().setTextAlign('center').run()}
                  className={editor.isActive({ textAlign: 'center' }) ? 'bg-muted' : ''}
                >
                  <AlignCenter size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Align center</TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => editor.chain().focus().setTextAlign('right').run()}
                  className={editor.isActive({ textAlign: 'right' }) ? 'bg-muted' : ''}
                >
                  <AlignRight size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Align right</TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => editor.chain().focus().setTextAlign('justify').run()}
                  className={editor.isActive({ textAlign: 'justify' }) ? 'bg-muted' : ''}
                >
                  <AlignJustify size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Justify</TooltipContent>
            </Tooltip>
            
            <Separator orientation="vertical" className="h-8" />

            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => editor.chain().focus().toggleBulletList().run()}
                  className={editor.isActive('bulletList') ? 'bg-muted' : ''}
                >
                  <List size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Bullet list</TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => editor.chain().focus().toggleOrderedList().run()}
                  className={editor.isActive('orderedList') ? 'bg-muted' : ''}
                >
                  <ListOrdered size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Numbered list</TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => editor.chain().focus().toggleBlockquote().run()}
                  className={editor.isActive('blockquote') ? 'bg-muted' : ''}
                >
                  <Quote size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Blockquote</TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                  className={editor.isActive('codeBlock') ? 'bg-muted' : ''}
                >
                  <Code size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Code block</TooltipContent>
            </Tooltip>
            
            <Separator orientation="vertical" className="h-8" />

            <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm"
                  className={editor.isActive('link') ? 'bg-muted' : ''}
                >
                  <LinkIcon size={16} />
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Insert link</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="space-y-2">
                    <Label htmlFor="link-url">URL</Label>
                    <Input
                      id="link-url"
                      value={linkUrl}
                      onChange={(e) => setLinkUrl(e.target.value)}
                      placeholder="https://example.com"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setLinkDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={insertLink}>Insert</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            
            <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="ghost" size="sm">
                  <ImageIcon size={16} />
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Insert image</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <Tabs defaultValue="upload">
                    <TabsList>
                      <TabsTrigger value="upload">Upload</TabsTrigger>
                      <TabsTrigger value="url">URL</TabsTrigger>
                    </TabsList>
                    <TabsContent value="upload" className="space-y-4 pt-2">
                      <div className="space-y-2">
                        <Label htmlFor="image-file">Choose image</Label>
                        <Input
                          id="image-file"
                          type="file"
                          accept="image/*"
                          onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="image-alt-upload">Alt text (important for SEO)</Label>
                        <Input
                          id="image-alt-upload"
                          value={imageAlt}
                          onChange={(e) => setImageAlt(e.target.value)}
                          placeholder="Descriptive image text"
                        />
                      </div>
                    </TabsContent>
                    <TabsContent value="url" className="space-y-4 pt-2">
                      <div className="space-y-2">
                        <Label htmlFor="image-url">Image URL</Label>
                        <Input
                          id="image-url"
                          value={imageUrl}
                          onChange={(e) => setImageUrl(e.target.value)}
                          placeholder="https://example.com/image.jpg"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="image-alt-url">Alt text (important for SEO)</Label>
                        <Input
                          id="image-alt-url"
                          value={imageAlt}
                          onChange={(e) => setImageAlt(e.target.value)}
                          placeholder="Descriptive image text"
                        />
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setImageDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleImageUpload} disabled={isUploading}>
                    {isUploading ? 'Uploading...' : 'Insert'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            
            <Dialog open={youtubeDialogOpen} onOpenChange={setYoutubeDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="ghost" size="sm">
                  <Youtube size={16} />
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Insert YouTube video</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="space-y-2">
                    <Label htmlFor="youtube-url">YouTube URL</Label>
                    <Input
                      id="youtube-url"
                      value={youtubeUrl}
                      onChange={(e) => setYoutubeUrl(e.target.value)}
                      placeholder="https://www.youtube.com/watch?v=dQw4w9WgXcQ"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setYoutubeDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={insertYoutubeVideo}>Insert</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={insertTable}
                >
                  <TableIcon size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Insert table</TooltipContent>
            </Tooltip>

            <Separator orientation="vertical" className="h-8" />
            
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="sm">
                  <Plus size={16} className="mr-1" /> Insert
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-48">
                <div className="flex flex-col space-y-1">
                  <Button variant="ghost" onClick={addHorizontalRule} className="justify-start">
                    <Minus size={16} className="mr-2" /> Horizontal line
                  </Button>
                  <Button variant="ghost" onClick={addInfoCallout} className="justify-start">
                    <Info size={16} className="mr-2" /> Info callout
                  </Button>
                  <Button variant="ghost" onClick={addWarningCallout} className="justify-start">
                    <AlertTriangle size={16} className="mr-2" /> Warning callout
                  </Button>
                  <Button variant="ghost" onClick={addSuccessCallout} className="justify-start">
                    <CheckCircle size={16} className="mr-2" /> Success callout
                  </Button>
                  <Button variant="ghost" onClick={addAccordion} className="justify-start">
                    <ChevronDown size={16} className="mr-2" /> Toggle content
                  </Button>
                  <Button variant="ghost" onClick={addButton} className="justify-start">
                    <Button size="sm" className="mr-2 p-0 h-auto" /> Button
                  </Button>
                </div>
              </PopoverContent>
            </Popover>

            <Separator orientation="vertical" className="h-8" />

            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => editor.chain().focus().undo().run()}
                  disabled={!editor.can().undo()}
                >
                  <Undo size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Undo</TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => editor.chain().focus().redo().run()}
                  disabled={!editor.can().redo()}
                >
                  <Redo size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Redo</TooltipContent>
            </Tooltip>

            <div className="ml-auto">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setFullScreen(!fullScreen)}
                  >
                    <Maximize size={16} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Fullscreen</TooltipContent>
              </Tooltip>
            </div>
          </div>
        </TooltipProvider>
      </div>
      
      {editor.isActive('table') && (
        <div className="bg-muted/10 border-b p-1">
          <div className="flex gap-1">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => editor.chain().focus().insertContent('<th></th>', {
                updateSelection: false,
              }).run()}
            >
              Add column before
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => editor.chain().focus().insertContent('<th></th>', {
                updateSelection: false,
              }).run()}
            >
              Add column after
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => {
                const html = editor.getHTML();
                const newHtml = html.replace(/<td>[^<]*<\/td>/, '');
                editor.commands.setContent(newHtml);
              }}
            >
              Delete column
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => editor.chain().focus().insertContent('<tr><td></td><td></td><td></td></tr>', {
                updateSelection: false,
              }).run()}
            >
              Add row before
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => editor.chain().focus().insertContent('<tr><td></td><td></td><td></td></tr>', {
                updateSelection: false,
              }).run()}
            >
              Add row after
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => {
                const html = editor.getHTML();
                const newHtml = html.replace(/<tr>.*?<\/tr>/, '');
                editor.commands.setContent(newHtml);
              }}
            >
              Delete row
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => {
                editor.commands.deleteNode('table');
              }}
            >
              Delete table
            </Button>
          </div>
        </div>
      )}
      
      <div className="p-4">
        <EditorContent 
          editor={editor} 
          className="prose dark:prose-invert max-w-none focus:outline-none min-h-[200px]" 
        />
      </div>
    </div>
  );
};
